import { Divider, Group, Menu, Stack, Title } from '@mantine/core';
import IconButton from '@ui/components/IconButton/IconButton';
import Icon from '@ui/components/Icon/Icon';
import Text from '@ui/components/Text/Text';
import ShareProjectModal from '@projects/modals/ShareProjectModal/ShareProjectModal';

import Project from 'src/entities/workspaceProject';

import './WorkspaceProjectListItem.scss';
import useWorkspaceProjectListItem from './useWorkspaceProjectListItem';

export type WorkspaceProjectListItemProps = {
	project: Project;
	currentUserIsWorkspaceOwner: boolean;
};

const WorkspaceProjectListItem = ( { project, currentUserIsWorkspaceOwner }: WorkspaceProjectListItemProps ) => {
	const {
		usersCount,
		showingShareProjectModal,
		setShowingShareProjectModal,
		canShareProject,
		canDeleteProject,
		canLeaveProject,
		canAccessProjectAsManager,
		createdAt,
		projectName,
		ownerName,
		onDeleteProjectClick,
		onLeaveProjectClick,
		onAccessProjectAsManagerClick
	} = useWorkspaceProjectListItem( { project, currentUserIsWorkspaceOwner } );

	return (
		<Group w="100%" spacing={2} ml="4px" p="8px 0 8px 8px" position="apart" noWrap>
			<Group noWrap spacing={16} style={{ flex: 1 }}>
				<img
					src={project.coverImageURL}
					alt={project.name}
					style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
				/>
				<Stack spacing={0}>
					<Title order={6}>{projectName}</Title>
					<Text lineClamp={1} color="gray.6">Owned by <strong>{ownerName}</strong></Text>
				</Stack>
			</Group>
			<Text w="90px" pl="2px" variant="footnote">{createdAt}</Text>
			<Text w="90px" variant="footnote">{usersCount}</Text>
			<Text w="90px" variant="footnote">{project.storageUsedInBytes?.formatBytes() ?? ' ... '}</Text>
			<Text w="90px" variant="footnote">{project.currentRoleId.replace( 'Project', '' ).capitalize()}</Text>
			<Divider orientation="vertical" color="var(--color-outline-default)" />
			<Menu withinPortal position="bottom-end" offset={5}>
				<Menu.Target>
					<IconButton icon="more_vert" size={20} color="var(--color-text-primary)" w="40px" miw="40px" aria-label="More options" />
				</Menu.Target>
				<Menu.Dropdown miw={160}>
					{canShareProject && (
						<Menu.Item icon={<Icon icon="person-add-outlined" size={14} color="var(--color-text-primary)" />} onClick={() => setShowingShareProjectModal( true )}>
							Share Project
						</Menu.Item>
					)}
					{canAccessProjectAsManager && (
						<Menu.Item icon={<Icon icon="add-circle-outlined" size={14} color="var(--color-text-primary)" />} onClick={() => onAccessProjectAsManagerClick()}>
							Access Project
						</Menu.Item>
					)}
					<Divider color="var(--color-outline-default)" my={2} />
					{canLeaveProject && (
						<Menu.Item icon={<Icon icon="leave" size={14} />} color="red" onClick={onLeaveProjectClick}>
							Leave Project
						</Menu.Item>
					)}
					{canDeleteProject && (
						<Menu.Item icon={<Icon icon="delete_outlined" size={14} />} color="red" onClick={onDeleteProjectClick}>
							Delete Project
						</Menu.Item>
					)}
				</Menu.Dropdown>
			</Menu>
			{showingShareProjectModal && (
				<ShareProjectModal
					project={project}
					onClose={() => setShowingShareProjectModal( false )}
				/>
			)}
		</Group>
	);
};

export default WorkspaceProjectListItem;
